import type { TokenCookie } from '@/types/cookies';
import type { DeferredPromise } from '@/composables/DeferredPromise';
import type { AppBridgeState, EntityParam } from '@/modules/app-bridge';

const MESSAGES = {
  NO_WINDOW: 'Window undefined, are you sure this is running client-side? Skipping.' as const,
  OS_NONE: 'No app OS detected.' as const,
  OS_ANDROID: 'App OS Detected - Android.' as const,
  OS_IOS: 'App OS Detected - iOS.' as const,

  BRIDGE_NOT_AVAILABLE: 'AppBridge is not initialized but tried calling action.' as const,
  BRIDGE_NOT_INITIALIZED: 'AppBridge is not initialized but tried resolving callback.' as const,

  CDC_UNSUPPORTED_LOGIN: 'CDC login not supported' as const,
  CDC_UNSUPPORTED_REGISTRATION: 'CDC registration not supported' as const,
  CDC_UNSUPPORTED_TOKEN_REFRESH: 'CDC token refresh not supported' as const,

  TOKEN_NULL: 'Token null' as const,
  UNSUPPORTED_ENTITY: 'Unsupported entity' as const,

  STATE_CHANGED: 'State changed' as const,
};

const BRIDGE_TYPE = {
  NONE: 'NONE' as const,
  ANDROID: 'ANDROID' as const,
  IOS: 'IOS' as const,
};

// Stubbing AppBridge during SSR to provide an object which
// can be called without additional conditionals or guards
export const createAppBridgeSSRStub = () => {
  const fn = () => {};
  return {
    isActive: false,
    isActiveCDC: false,
    init: fn,
    actions: {
      login: fn,
      register: fn,
      requestToken: fn,
      logout: fn,
      notify: fn,
    },
    callbacks: {
      login: fn,
      register: fn,
      refreshAuthToken: fn,
    },
  };
};

export const createAppBridge = () => {
  const $log = logger.createLogMethods('app-bridge');

  const state = reactive<AppBridgeState>({
    bridgeType: BRIDGE_TYPE.NONE,
    initialized: false,
    supportsCDC: false,

    androidBridge: undefined,
    iOSBridge: undefined,

    login: undefined,
    register: undefined,
    refreshAuthToken: undefined,
  });

  // region Internal Methods
  const encodeToken = (entityParam?: EntityParam) => {
    return JSON.stringify(toRaw(entityParam));
  };

  const isFullToken = (entityParam?: EntityParam) => {
    return !!(entityParam?.access_token && entityParam?.refresh_token);
  };

  const defineDeferredResolveCallback = (deferredPromise: () => DeferredPromise | undefined) => {
    return async (value: any): Promise<boolean | undefined> => {
      if (!state.initialized) {
        $log.debug(MESSAGES.BRIDGE_NOT_INITIALIZED);
        return false;
      }

      return deferredPromise?.()?.resolveIfValue(value);
    };
  };

  const useStateChangedWatcher = () => {
    // Usually this should only run client-side, but better
    // make double-sure this watcher doesn't leak during SSR
    if (!import.meta.client) {
      return;
    }

    let from = deepClone(toRaw(state));

    watchDeep(
      () => state,
      async (to) => {
        const { detailedDiff } = await import('deep-object-diff');
        const toClone = deepClone(toRaw(to));
        $log.debug(MESSAGES.STATE_CHANGED, toClone, detailedDiff(from, toClone));
        from = toClone;
      },
    );
  };

  const detectBridgeType = () => {
    if (import.meta.server) {
      state.bridgeType = BRIDGE_TYPE.NONE;
      return;
    }

    if (typeof window === 'undefined') {
      $log.debug(MESSAGES.NO_WINDOW);
      state.bridgeType = BRIDGE_TYPE.NONE;
      return;
    }

    if (window.FressnapfWebInterface || window.CdcWebInterface) {
      state.bridgeType = BRIDGE_TYPE.ANDROID;
      return;
    }

    if (window.webkit?.messageHandlers?.fressnapfJSHandler || window.webkit?.messageHandlers?.cdcJSHandler) {
      state.bridgeType = BRIDGE_TYPE.IOS;
      return;
    }

    state.bridgeType = BRIDGE_TYPE.NONE;
  };
  // endregion

  // region Storefront Actions
  /**
   * Detects the current mobile OS and prepares the appropriate states.
   */
  const init = () => {
    detectBridgeType();

    // Reset for good measure
    state.iOSBridge = undefined;
    state.androidBridge = undefined;

    // Update OS specific state
    switch (state.bridgeType) {
      case BRIDGE_TYPE.ANDROID:
        $log.debug(MESSAGES.OS_ANDROID);
        state.androidBridge = {
          supportsCDC: !!window.CdcWebInterface,
        };
        break;

      case BRIDGE_TYPE.IOS:
        $log.debug(MESSAGES.OS_IOS);
        state.iOSBridge = {
          fnInterface: window.webkit?.messageHandlers?.fressnapfJSHandler,
          cdcInterface: window.webkit?.messageHandlers?.cdcJSHandler,
          supportsCDC: !!window.webkit?.messageHandlers?.cdcJSHandler,
        };
        break;

      default:
        $log.debug(MESSAGES.OS_NONE);
        break;
    }

    // Update shared state

    // TODO: Add feature toggle to bypass CDC functionality
    state.supportsCDC = (state.androidBridge?.supportsCDC || state.iOSBridge?.supportsCDC) ?? false;
    state.initialized = !!(state.androidBridge || state.iOSBridge);
  };

  /**
   * Requests app to log a user in and provide a new token.
   */
  const login = async (): Promise<TokenCookie | undefined> => {
    if (!state.initialized) {
      throw new Error(MESSAGES.BRIDGE_NOT_AVAILABLE);
    }

    if (!state.supportsCDC) {
      throw new Error(MESSAGES.CDC_UNSUPPORTED_LOGIN);
    }

    // Android CDC based flow
    if (window.CdcWebInterface) {
      const token = await window.CdcWebInterface.login();
      if (!token) {
        throw new Error(MESSAGES.TOKEN_NULL);
      }
      return token;
    }

    // iOS CDC based flow
    if (state.iOSBridge?.cdcInterface) {
      state.login = useDeferredPromise<TokenCookie>();
      state.iOSBridge.cdcInterface.postMessage({ command: 'login' });
      return state.login.promise;
    }
  };

  /**
   * Requests app to register a user and provide a new token.
   */
  const register = async (): Promise<TokenCookie | undefined> => {
    if (!state.initialized) {
      throw new Error(MESSAGES.BRIDGE_NOT_AVAILABLE);
    }

    if (!state.supportsCDC) {
      throw new Error(MESSAGES.CDC_UNSUPPORTED_REGISTRATION);
    }

    // Android CDC based flow
    if (window.CdcWebInterface) {
      const token = await window.CdcWebInterface.register();
      if (!token) {
        throw new Error(MESSAGES.TOKEN_NULL);
      }
      return token;
    }

    // iOS CDC based flow
    if (state.iOSBridge?.cdcInterface) {
      state.register = useDeferredPromise<TokenCookie>();
      state.iOSBridge.cdcInterface.postMessage({ command: 'register' });
      return state.register.promise;
    }
  };

  /**
   * Requests app to provide a new token.
   */
  const requestToken = async (): Promise<TokenCookie | undefined> => {
    if (!state.initialized) {
      throw new Error(MESSAGES.BRIDGE_NOT_AVAILABLE);
    }

    if (!state.supportsCDC) {
      throw new Error(MESSAGES.CDC_UNSUPPORTED_TOKEN_REFRESH);
    }

    // Android CDC based flow
    if (window.CdcWebInterface) {
      const token = await window.CdcWebInterface.refreshAuthToken();
      if (!token) {
        throw new Error(MESSAGES.TOKEN_NULL);
      }
      return token;
    }

    // iOS CDC based flow
    if (state.iOSBridge?.cdcInterface) {
      state.refreshAuthToken = useDeferredPromise<TokenCookie>();
      state.iOSBridge.cdcInterface.postMessage({ command: 'refreshAuthToken' });
      return state.refreshAuthToken.promise;
    }
  };

  /**
   * Requests app to log a user out.
   */
  const logout = async (): Promise<boolean> => {
    if (!state.initialized) {
      return false;
    }

    // CDC based flow
    if (state.supportsCDC) {
      window.CdcWebInterface?.onLogout();
      state.iOSBridge?.cdcInterface?.postMessage({ command: 'logout' }); // No typo, only iOS renamed to 'logout'
      return true;
    }

    // Legacy non-CDC flow
    window.FressnapfWebInterface?.onLogout();
    state.iOSBridge?.fnInterface?.postMessage({ command: 'onLogout' });
    return true;
  };

  /**
   * Notify app for different events.
   */
  const notify = (changedEntity: string, entityParam?: EntityParam) => {
    switch (changedEntity) {
      case 'user':
        window.FressnapfWebInterface?.notifyUserChanged();
        state.iOSBridge?.fnInterface?.postMessage({ command: 'notifyUserChanged' });
        break;

      case 'cart':
        window.FressnapfWebInterface?.notifyCartChanged();
        state.iOSBridge?.fnInterface?.postMessage({ command: 'notifyCartChanged' });
        break;

      case 'startCheckout':
        window.FressnapfWebInterface?.startCheckout();
        state.iOSBridge?.fnInterface?.postMessage({ command: 'startCheckout' });
        break;

      case 'onCheckoutSuccessful':
        window.FressnapfWebInterface?.onCheckoutSuccessful();
        state.iOSBridge?.fnInterface?.postMessage({ command: 'onCheckoutSuccessful' });
        break;

      // Skipping this case when CDC is supported, no need to notify the app after it provided a token to us
      case 'onNewToken':
        if (state.supportsCDC) {
          return;
        }

        if (isFullToken(entityParam)) {
          const encodedParam = encodeToken(entityParam);

          window.FressnapfWebInterface?.onNewToken(encodedParam);
          state.iOSBridge?.fnInterface?.postMessage({ command: 'onNewToken', session: entityParam });
        }
        break;

      default:
        $log.debug(MESSAGES.UNSUPPORTED_ENTITY, { changedEntity, entityParam });
        break;
    }
  };
  // endregion

  if (import.meta.client) {
    useStateChangedWatcher();
    init();
  }

  return {
    /**
     * Is true when global legacy bridge objects (FressnapfWebInterface, fressnapfJSHandler) have been detected.
     */
    get isActive() {
      return state.initialized;
    },

    /**
     * Is true when global CDC bridge objects (CdcWebInterface, cdcJSHandler) have been detected.
     */
    get isActiveCDC() {
      return state.initialized && state.supportsCDC;
    },

    /**
     * Currently active bridge type.
     */
    get bridgeType() {
      return state.bridgeType;
    },

    init,

    /**
     * Actions to be used by storefront.
     */
    actions: {
      login,
      register,
      requestToken,
      logout,
      notify,
    },

    /**
     * Optional callbacks to be used by app.
     *
     * These callbacks are used to resolve (or reject) a deferred promise, signaling the completion of async
     * actions which have previously been triggered by a non-blocking function invocation in the storefront.
     */
    callbacks: {
      login: defineDeferredResolveCallback(() => state.login),
      register: defineDeferredResolveCallback(() => state.register),
      refreshAuthToken: defineDeferredResolveCallback(() => state.refreshAuthToken),
    },
  };
};

export type AppBridge = ReturnType<typeof createAppBridge>;

export const useAppBridge = () => useNuxtApp().$appBridge as AppBridge;
